/**
 * The public link for one document: the address a client opens without signing
 * in, and the same address the QR code on the printed page points at.
 *
 * The link is minted on demand, never on page load. A document nobody ever
 * shared has no public address at all, which is the state every one of the
 * several thousand imported rows should stay in until somebody decides
 * otherwise - so this card READS on mount and only writes when pressed.
 *
 * Rotating and revoking are both here because a link that cannot be taken back
 * is not a link anybody should hand out. Both are confirmed first: they break
 * every copy of the address at once, including the one already printed on paper
 * and the one in a mail sent last week.
 */
import { useCallback, useEffect, useState } from "react";
import { Link2, Loader2, RefreshCw, Trash2, Copy } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import {
  createPublicDocLink, getPublicDocLink, revokePublicDocLink, rotatePublicDocLink,
  type PublicDocLink, type SalesShareType,
} from "../../../lib/sales";
import { Btn, Card, CardHead, Modal, useCopyToast } from "../../ui";

type Pending = "rotate" | "revoke" | null;

export function PublicLinkCard({
  type,
  id,
  canManage,
}: {
  type: SalesShareType;
  id: string;
  canManage: boolean;
}) {
  const t = useT();
  const copy = useCopyToast();
  const [link, setLink] = useState<PublicDocLink | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<Pending>(null);

  const load = useCallback(() => {
    getPublicDocLink(type, id).then(setLink).catch(() => setLink(null));
  }, [type, id]);
  useEffect(load, [load]);

  const run = async (fn: () => Promise<PublicDocLink>) => {
    setBusy(true);
    try {
      setLink(await fn());
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  return (
    <Card className="mt-4">
      <CardHead title={t("sales.share.title")} sub={t("sales.share.sub")} />

      <div className="px-[26px] pb-7 pt-1">
        {link?.url ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {/* Read-only rather than a plain span: an operator's first move is
                  to select the address, and a real input makes that work with a
                  keyboard as well as with the copy button. */}
              <label className="crm-inp min-w-0 flex-1">
                <input
                  readOnly
                  value={link.url}
                  aria-label={t("sales.share.title")}
                  onFocus={(e) => e.currentTarget.select()}
                />
              </label>
              <Btn small onClick={() => copy(link.url ?? "")}>
                <Copy className="size-4" />
                {t("sales.share.copy")}
              </Btn>
              {canManage ? (
                <>
                  <Btn small onClick={() => setPending("rotate")} disabled={busy}>
                    <RefreshCw className="size-4" />
                    {t("sales.share.rotate")}
                  </Btn>
                  <Btn small tone="danger" onClick={() => setPending("revoke")} disabled={busy}>
                    <Trash2 className="size-4" />
                    {t("sales.share.revoke")}
                  </Btn>
                </>
              ) : null}
            </div>
            <p className="crm-mut mt-2.5 text-[12.5px] leading-relaxed">{t("sales.share.qrNote")}</p>
            {link.visible ? null : (
              <p className="mt-1.5 text-[12.5px] leading-relaxed text-amber-600">{t("sales.share.draftWarning")}</p>
            )}
          </>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <p className="crm-mut min-w-0 flex-1 text-[13px] leading-relaxed">{t("sales.share.none")}</p>
            {canManage ? (
              <Btn small tone="dark" onClick={() => run(() => createPublicDocLink(type, id))} disabled={busy}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : <Link2 className="size-4" />}
                {t("sales.share.create")}
              </Btn>
            ) : null}
          </div>
        )}
      </div>

      {pending ? (
        <Modal
          title={t(pending === "rotate" ? "sales.share.rotate" : "sales.share.revoke")}
          label={t(pending === "rotate" ? "sales.share.rotate" : "sales.share.revoke")}
          onClose={() => setPending(null)}
          footer={
            <>
              <Btn onClick={() => setPending(null)}>{t("common.cancel")}</Btn>
              <Btn
                tone="danger"
                onClick={() =>
                  run(() => (pending === "rotate" ? rotatePublicDocLink(type, id) : revokePublicDocLink(type, id)))
                }
              >
                {t(pending === "rotate" ? "sales.share.rotate" : "sales.share.revoke")}
              </Btn>
            </>
          }
        >
          <p className="crm-mut text-[13.25px] leading-relaxed">
            {t(pending === "rotate" ? "sales.share.rotateConfirm" : "sales.share.revokeConfirm")}
          </p>
        </Modal>
      ) : null}
    </Card>
  );
}
